import { Injectable } from '@angular/core';

/**
 * Something on screen that can hold unsaved changes while support impersonation is in edit mode.
 * `save` must resolve only once the server has confirmed the save (true) or refused it (false): a
 * save that has merely been sent is not a success (#3335 S6).
 */
export interface SupportImpersonationDraft {
  hasUnsavedChanges(): boolean;
  save(): Promise<boolean>;
  discard(): void;
}

/**
 * Editors register their unsaved state here so that a deliberate switch out of edit mode can offer
 * Save, Discard or Cancel (#3335 S6). Registration is presentation only: the API refuses every change
 * once the edit context has ended, whatever is registered.
 */
@Injectable({ providedIn: 'root' })
export class SupportImpersonationDrafts {
  private readonly _drafts = new Set<SupportImpersonationDraft>();

  /** Register a draft; call the returned function to unregister (for example from a DestroyRef). */
  register(draft: SupportImpersonationDraft): () => void {
    this._drafts.add(draft);
    return () => this._drafts.delete(draft);
  }

  /** The registered drafts that hold unsaved changes now. */
  unsaved(): SupportImpersonationDraft[] {
    return [...this._drafts].filter((draft) => draft.hasUnsavedChanges());
  }
}
